import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { AppLink, Button, Card, ErrorText, Flex, Page, PageHeader, Text } from '@cmaas/ui';
import type { AdminProperty } from '../../api/types';
import { apiFetch, CmaasApiError } from '../../api/client';

const mutationError = (err: unknown) => (err instanceof CmaasApiError ? err.message : 'Something went wrong.');

export function PropertyDetailPage() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const queryClient = useQueryClient();

  const propertyQuery = useQuery({
    queryKey: ['property', propertyId],
    queryFn: () => apiFetch<AdminProperty>(`/v1/admin/properties/${propertyId}`),
  });

  const toggleStatus = useMutation({
    mutationFn: ({ status }: { status: 'active' | 'disabled' }) =>
      apiFetch(`/v1/admin/properties/${propertyId}/actions/${status === 'active' ? 'disable' : 'enable'}`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['property', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });

  if (propertyQuery.isLoading) return <Text as="p">Loading…</Text>;
  if (!propertyQuery.data) return <Text as="p">Property not found.</Text>;
  const property = propertyQuery.data;

  return (
    <Page>
      <PageHeader
        title={property.name}
        actions={
          <Button
            type="button"
            variant="soft"
            size="1"
            onClick={() => toggleStatus.mutate({ status: property.status })}
            disabled={toggleStatus.isPending}
          >
            {property.status === 'active' ? 'Disable property' : 'Enable property'}
          </Button>
        }
      />
      <Card>
        <Flex direction="column" gap="2">
          <Text as="p">
            <Text weight="bold">Status:</Text> {property.status}
          </Text>
          <Text as="p">
            <Text weight="bold">Tenant:</Text> <AppLink to={`/tenants/${property.tenant_id}`}>{property.tenant_id}</AppLink>
          </Text>
          <Text as="p">
            <Text weight="bold">Apple platform:</Text> {property.apple_platform}
          </Text>
          <Text as="p">
            <Text weight="bold">Timezone:</Text> {property.timezone}
          </Text>
          <Text as="p">
            <Text weight="bold">Created:</Text> {new Date(property.created_at).toLocaleString()}
          </Text>
        </Flex>
      </Card>
      {toggleStatus.isError && <ErrorText>{mutationError(toggleStatus.error)}</ErrorText>}
    </Page>
  );
}
